import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, ArrowRight, Globe, Bot, Star, Search, BarChart3, Zap, Building2, TrendingUp, Users, Award, Phone, Clock, Shield, Play } from 'lucide-react';

const Solutions = () => {
  const handleBookDemo = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleStartTrial = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScheduleCall = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const packages = [
    {
      id: 2,
      name: "Growth Accelerator",
      subtitle: "Already have a website? Add AI automation and local dominance.",
      features: [
        "Isabella AI assistant integration with your existing site",
        "Automated review collection and management",
        "Local SEO optimization",
        "Growth Command Center dashboard",
        "Email support (24-hour response)"
      ],
      setupPrice: "$1,197",
      monthlyPrice: "$397",
      timeline: "2-3 weeks (we build everything custom for your restaurant)",
      bestFor: "Restaurants with existing websites who want AI automation and better local visibility.",
      gradient: "from-forest-primary to-forest-primary-600",
      bgGradient: "from-forest-primary/10 to-forest-primary-600/10",
      icon: Zap,
      popular: false
    },
    {
      id: 1,
      name: "Complete Restaurant System",
      badge: "Most Popular",
      subtitle: "Perfect if you need a full rebuild and growth engine.",
      features: [
        "Custom website designed for conversions",
        "Isabella AI assistant for 24/7 reservations",
        "Automated review collection and management",
        "Local SEO optimization",
        "Growth Command Center dashboard",
        "White-glove onboarding and training",
        "Priority support (4-hour response)"
      ],
      setupPrice: "$2,497",
      monthlyPrice: "$597",
      setupNote: "waived on annual plans",
      timeline: "2-3 weeks from contract to launch (we build everything custom for your restaurant)",
      bestFor: "Restaurants needing a complete online transformation or starting from scratch.",
      gradient: "from-forest-primary to-sienna-accent",
      bgGradient: "from-forest-primary/10 to-sienna-accent/10",
      icon: Building2,
      popular: true
    },
    {
      id: 3,
      name: "Multi-Location Package",
      subtitle: "Managing 3+ locations? Get custom pricing and dedicated support.",
      features: [
        "Everything in Complete Restaurant System",
        "Multi-location dashboard",
        "Dedicated account manager",
        "Custom integrations",
        "Phone support",
        "Quarterly strategy reviews"
      ],
      setupPrice: "Custom pricing",
      monthlyPrice: "based on location count",
      timeline: "Custom timeline based on requirements",
      bestFor: "Restaurant groups with multiple locations needing centralized management.",
      gradient: "from-brand-gold to-brand-gold-600",
      bgGradient: "from-brand-gold/10 to-brand-gold-600/10",
      icon: Users,
      popular: false,
      isCustom: true
    }
  ];

  const guarantees = [
    { icon: Shield, text: "30-day money-back guarantee", color: "from-forest-primary to-forest-primary-600" },
    { icon: Users, text: "White-glove setup", color: "from-forest-primary to-forest-primary-600" },
    { icon: Clock, text: "Fast implementation", color: "from-sienna-accent to-sienna-accent-600" }
  ];

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-restaurant-dark-900 via-restaurant-dark-800 to-restaurant-dark-700">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-forest-primary/20 to-forest-primary-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-br from-sienna-accent/20 to-brand-gold/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
            Choose Your 
            <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent">
              Growth Path
            </span>
          </h1>
          <p className="text-xl text-brand-ivory/90 max-w-4xl mx-auto mb-12 leading-relaxed">
            Both plans include 30-day money-back guarantee and white-glove setup. The only difference is whether you need a new website.
          </p>
          
          {/* Guarantees */}
          <div className="flex flex-wrap justify-center items-center gap-8 mb-16">
            {guarantees.map((guarantee, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 border border-white/20">
                <div className={`w-8 h-8 bg-gradient-to-r ${guarantee.color} rounded-full flex items-center justify-center`}>
                  <guarantee.icon className="text-white" size={16} />
                </div>
                <span className="text-white font-medium">{guarantee.text}</span>
              </div>
            ))}
          </div>

          {/* Simple Visual Element */}
          <div className="mt-16 flex justify-center">
            <div className="relative">
              {/* Three Package Cards Preview */}
              <div className="flex items-center justify-center space-x-6">
                <div className="w-20 h-32 bg-gradient-to-br from-forest-primary/20 to-forest-primary-600/20 rounded-2xl backdrop-blur-sm border border-forest-primary/30 transform rotate-12 animate-float"></div>
                <div className="w-20 h-36 bg-gradient-to-br from-forest-primary/20 to-sienna-accent/20 rounded-2xl backdrop-blur-sm border border-sienna-accent/30 transform -rotate-6 animate-float-delayed scale-110"></div>
                <div className="w-20 h-32 bg-gradient-to-br from-brand-gold/20 to-forest-primary/20 rounded-2xl backdrop-blur-sm border border-forest-primary/30 transform rotate-12 animate-float"></div>
              </div>
              
              {/* Arrow pointing down */}
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 animate-bounce">
                <ArrowRight className="w-6 h-6 transform rotate-90" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-32 bg-gradient-to-br from-white via-brand-ivory to-white relative overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-forest-primary/10 to-forest-primary-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-br from-sienna-accent/10 to-brand-gold/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {packages.map((pkg, index) => (
              <div key={pkg.id} className={`group relative transition-all duration-700 transform hover:-translate-y-6 hover:scale-105 ${
                pkg.popular ? 'modern-pricing-card-premium' : 
                pkg.isCustom ? 'modern-pricing-card-enterprise' : 'modern-pricing-card'
              }`}>
                <Card className="relative border-0 bg-transparent shadow-none m-6 z-10">
                {/* Popular Badge */}
                {pkg.popular && (
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-30">
                    <Badge className="bg-gradient-to-r from-forest-primary to-sienna-accent text-white px-6 py-2 text-sm font-semibold shadow-lg">
                      {pkg.badge}
                    </Badge>
                  </div>
                )}
                
                {/* Subtle hover background effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${pkg.bgGradient} opacity-0 group-hover:opacity-30 transition-all duration-500 rounded-xl`}></div>
                
                <CardHeader className="relative z-10 pb-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${pkg.gradient} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg`}>
                      <pkg.icon className="text-white" size={28} />
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-brand-charcoal group-hover:bg-gradient-to-r group-hover:from-brand-charcoal-700 group-hover:to-brand-charcoal group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                        {pkg.name}
                      </CardTitle>
                    </div>
                  </div>
                  
                  <p className="text-brand-charcoal-600 leading-relaxed mb-6">{pkg.subtitle}</p>
                </CardHeader>

                <CardContent className="relative z-10 space-y-8">
                  {/* What's Included */}
                  <div>
                    <h3 className="text-lg font-semibold text-brand-charcoal mb-4">What's Included:</h3>
                    <ul className="space-y-3">
                      {pkg.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-3 group-hover:translate-x-2 transition-transform duration-300" style={{transitionDelay: `${featureIndex * 50}ms`}}>
                          <div className="w-5 h-5 bg-gradient-to-r from-forest-primary to-forest-primary-600 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                            <CheckCircle className="text-white" size={12} />
                          </div>
                          <span className="text-brand-charcoal-600 group-hover:text-brand-charcoal transition-colors duration-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Investment */}
                  <div className="space-y-4 bg-brand-ivory/50 rounded-2xl p-6 group-hover:bg-white/50 transition-all duration-300">
                    <h3 className="text-lg font-semibold text-brand-charcoal">Investment:</h3>
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <span className="text-brand-charcoal-600 mt-2">Setup:</span>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-brand-charcoal leading-tight">{pkg.setupPrice}</div>
                          {pkg.setupNote && <div className="text-sm text-brand-charcoal-500 mt-1">({pkg.setupNote})</div>}
                        </div>
                      </div>
                      <div className="flex items-start justify-between">
                        <span className="text-brand-charcoal-600 mt-2">Monthly:</span>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-brand-charcoal leading-tight">{pkg.monthlyPrice}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Setup Timeline */}
                  <div>
                    <h4 className="font-semibold text-brand-charcoal mb-2">Setup Timeline:</h4>
                    <p className="text-sm text-brand-charcoal-600">{pkg.timeline}</p>
                  </div>

                  {/* Best For */}
                  <div>
                    <h4 className="font-semibold text-brand-charcoal mb-2">Best For:</h4>
                    <p className="text-sm text-brand-charcoal-600">{pkg.bestFor}</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-4 pt-4">
                    {pkg.isCustom ? (
                      <Button 
                        onClick={handleScheduleCall}
                        className="group relative w-full bg-[#2D6A4F] hover:bg-[#1F4A36] text-white font-bold text-base px-8 py-4 rounded-xl shadow-2xl shadow-[#2D6A4F]/25 hover:shadow-[#2D6A4F]/40 transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden"
                      >
                        {/* Animated background layer */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        {/* Shimmer effect */}
                        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
                        
                        {/* Content */}
                        <div className="relative z-10 flex items-center justify-center space-x-2">
                          <Phone className="w-5 h-5 group-hover:animate-pulse" />
                          <span>Schedule Discovery Call</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                        </div>
                        
                        {/* Glow effect */}
                        <div className="absolute inset-0 rounded-xl bg-[#2D6A4F]/50 blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500 -z-10 scale-110"></div>
                      </Button>
                    ) : (
                      <>
                        <Button 
                          onClick={handleStartTrial}
                          className="group relative w-full text-white font-bold text-base px-8 py-4 rounded-xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden"
                          style={{
                            backgroundColor: '#D4623C',
                            color: '#FFFFFF',
                            boxShadow: '0 25px 50px -12px rgba(212, 98, 60, 0.25)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#B8532F';
                            e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#D4623C';
                            e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.25)';
                          }}
                        >
                          {/* Animated background layer */}
                          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          
                          {/* Shimmer effect */}
                          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
                          
                          {/* Content */}
                          <div className="relative z-10 flex items-center justify-center space-x-2">
                            <Zap className="w-5 h-5 group-hover:animate-pulse" />
                            <span>Start 30-Day Risk-Free Trial</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                          </div>
                        </Button>
                        
                        <Button 
                          onClick={handleBookDemo}
                          className="group relative w-full font-semibold text-base px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] overflow-hidden"
                          style={{
                            backgroundColor: 'transparent',
                            border: '2px solid #2D6A4F',
                            color: '#2D6A4F'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.borderColor = '#1F4A36';
                            e.target.style.backgroundColor = 'rgba(45, 106, 79, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.borderColor = '#2D6A4F';
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          {/* Content */}
                          <div className="relative z-10 flex items-center justify-center space-x-2">
                            <span>Watch Demo</span>
                          </div>
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Not Sure Which Package Section */}
      <section className="py-32 bg-gradient-to-br from-restaurant-dark-900 via-restaurant-dark-800 to-restaurant-dark-700 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-gradient-to-br from-forest-primary/10 to-forest-primary-600/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-gradient-to-br from-sienna-accent/10 to-brand-gold/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-8">
            Not Sure Which 
            <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent">
              Package?
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-brand-ivory/90 max-w-3xl mx-auto mb-12 leading-relaxed">
            Book a free 15-minute consultation and we'll recommend the fastest path to growth for your restaurant.
          </p>
          
          <Button 
            onClick={handleScheduleCall}
            size="lg"
            className="group relative font-bold text-base sm:text-lg lg:text-xl px-6 sm:px-10 lg:px-12 py-4 sm:py-5 lg:py-6 rounded-2xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden w-full sm:w-auto"
            style={{
              backgroundColor: '#D4623C',
              color: '#FFFFFF',
              boxShadow: '0 25px 50px -12px rgba(212, 98, 60, 0.25)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#B8532F';
              e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#D4623C';
              e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.25)';
            }}
          >
            {/* Animated background layer */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
            
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center space-x-3">
              <Users className="w-6 h-6 group-hover:animate-pulse" />
              <span>Schedule Consultation</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
            
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-2xl bg-[#2D6A4F]/50 blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500 -z-10 scale-110"></div>
          </Button>
        </div>
      </section>

      {/* Contact Section Placeholder for Scroll Target */}
      <section id="contact" className="py-20 bg-gradient-to-br from-brand-ivory-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-brand-charcoal mb-8">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-brand-charcoal-600 mb-8 leading-relaxed">
            Contact us to book your demo or start your trial today.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-brand-charcoal/20">
            <p className="text-lg text-brand-charcoal-600 mb-4">
              📞 Phone: <span className="font-semibold">(555) 123-GROW</span>
            </p>
            <p className="text-lg text-brand-charcoal-600 mb-4">
              ✉️ Email: <span className="font-semibold">hello@prismospark.com</span>
            </p>
            <p className="text-base text-brand-charcoal-500">
              Our team is available Monday-Friday, 9 AM - 6 PM EST
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;